import { useCallback, useEffect, useRef } from 'react';

import type { LoggerHostDeps } from './hostDeps';
import type { UseHoldTimer } from './useHoldTimer';
import type { UseRestTimer } from './useRestTimer';

// THE one stop for every live clock of a workout: the rest, the hold/cardio
// count, the prescribed bout, and their notifications. A workout that ends
// (saved, queued offline, discarded, or replaced by a new one) must leave no
// countdown running and no bell to ring later. Before this, the save stopped
// the rest alone, so a hold or a bout kept its trigger, and Antenore got the
// "timer over" notification after he had finished.
//
// The web mount passes no `timerNotification`: there, stopping the in-app
// timers is all there is to do.
export function useStopAllTimers({
  restTimer,
  hold,
  timerNotification,
  sessionKey,
}: {
  restTimer: Pick<UseRestTimer, 'reset'>;
  hold: Pick<UseHoldTimer, 'cancel'>;
  timerNotification?: LoggerHostDeps['timerNotification'];
  /** draft.startedAt: changes once per session (initial(), hydrate(),
   *  reset()). The Logger stays mounted across workouts (the Modal toggles
   *  `visible`), so a new key is a new workout and the clocks must stop. */
  sessionKey: string;
}): () => void {
  // Read through a ref: `hold.cancel` is rebuilt on every render, and the
  // session effect below keys on this function, which must stay stable.
  const latest = useRef({ restTimer, hold, timerNotification });
  latest.current = { restTimer, hold, timerNotification };
  const stop = useCallback(() => {
    const cur = latest.current;
    cur.restTimer.reset();
    cur.hold.cancel();
    // After the two resets above, so it queues behind their cancels and also
    // clears a "time's up" already shown in the drawer.
    void cur.timerNotification?.cancelAll?.();
  }, []);
  useEffect(() => {
    stop();
  }, [sessionKey, stop]);
  return stop;
}
